import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Slider } from "@/components/ui/slider";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft,
  Mic,
  MicOff,
  Volume2,
  VolumeX,
  Sparkles,
  MessageSquare,
  Lightbulb,
  AlertTriangle,
  ThumbsUp,
  Target,
  Users,
  Plus,
  Trash2,
  Upload,
  Play,
  Settings,
  Database,
  Loader2,
  Radio,
  Building2,
  Globe,
  FileText,
  Brain,
  Network,
  Scale,
  RefreshCw,
  Eye,
  Link,
  User,
  Briefcase,
  AudioLines,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// WebRTC Audio Chat Class
class RealtimeAudioChat {
  constructor(onMessage, onConnectionChange) {
    this.peerConnection = null;
    this.dataChannel = null;
    this.audioElement = null;
    this.localStream = null;
    this.onMessage = onMessage;
    this.onConnectionChange = onConnectionChange;
    this.isConnected = false;
  }

  async init() {
    try {
      this.onConnectionChange?.("connecting");

      // Step 1: Get ephemeral token from backend
      console.log("[WebRTC] Fetching ephemeral token...");
      const tokenResponse = await fetch(`${BACKEND_URL}/api/realtime/session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" }
      });
      
      const tokenData = await tokenResponse.json();
      
      if (!tokenResponse.ok) {
        console.error("[WebRTC] Token fetch failed:", tokenData);
        throw new Error(tokenData.detail || "Failed to get session token");
      }
      
      const ephemeralToken = tokenData.value;
      console.log("[WebRTC] Got ephemeral token:", ephemeralToken?.substring(0, 20) + "...");
      
      if (!ephemeralToken) {
        throw new Error("No ephemeral token received from server");
      }

      // Step 2: Setup WebRTC
      console.log("[WebRTC] Setting up peer connection...");
      this.peerConnection = new RTCPeerConnection();
      this.setupAudioElement();
      
      // Step 3: Get microphone access
      console.log("[WebRTC] Requesting microphone access...");
      await this.setupLocalAudio();
      console.log("[WebRTC] Microphone access granted");
      
      this.setupDataChannel();

      // Step 4: Create offer
      console.log("[WebRTC] Creating offer...");
      const offer = await this.peerConnection.createOffer();
      await this.peerConnection.setLocalDescription(offer);

      // Step 5: Send offer directly to OpenAI with ephemeral token
      console.log("[WebRTC] Sending offer to OpenAI...");
      const sdpResponse = await fetch(
        "https://api.openai.com/v1/realtime/calls",
        {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${ephemeralToken}`,
            "Content-Type": "application/sdp",
          },
          body: offer.sdp,
        }
      );

      if (!sdpResponse.ok) {
        const errorText = await sdpResponse.text();
        console.error("[WebRTC] OpenAI error:", sdpResponse.status, errorText);
        throw new Error(`OpenAI connection failed (${sdpResponse.status}): ${errorText.substring(0, 100)}`);
      }

      const answerSdp = await sdpResponse.text();
      console.log("[WebRTC] Got SDP answer, setting remote description...");
      
      await this.peerConnection.setRemoteDescription({
        type: "answer",
        sdp: answerSdp
      });
      
      this.isConnected = true;
      this.onConnectionChange?.("connected");
      console.log("[WebRTC] Connection established!");
      
    } catch (error) {
      console.error("[WebRTC] Failed to initialize:", error);
      this.onConnectionChange?.("error");
      throw error;
    }
  }

  setupAudioElement() {
    this.audioElement = document.createElement("audio");
    this.audioElement.autoplay = true;
    document.body.appendChild(this.audioElement);
    this.peerConnection.ontrack = (event) => {
      this.audioElement.srcObject = event.streams[0];
    };
  }

  async setupLocalAudio() {
    try {
      this.localStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.localStream.getTracks().forEach(track => {
        this.peerConnection.addTrack(track, this.localStream);
      });
    } catch (err) {
      if (err.name === "NotFoundError") {
        throw new Error("No microphone found. Please connect a microphone and try again.");
      } else if (err.name === "NotAllowedError") {
        throw new Error("Microphone access denied. Please allow microphone access in your browser settings.");
      } else {
        throw new Error(`Microphone error: ${err.message}`);
      }
    }
  }

  setupDataChannel() {
    this.dataChannel = this.peerConnection.createDataChannel("oai-events");
    this.dataChannel.onmessage = (event) => {
      this.onMessage?.(JSON.parse(event.data));
    };
  }

  sendMessage(message) {
    if (this.dataChannel?.readyState === "open") {
      this.dataChannel.send(JSON.stringify(message));
    }
  }

  setMicEnabled(enabled) {
    this.localStream?.getAudioTracks().forEach(track => { track.enabled = enabled; });
  }

  setSpeakerEnabled(enabled) {
    if (this.audioElement) this.audioElement.muted = !enabled;
  }

  disconnect() {
    this.localStream?.getTracks().forEach(track => track.stop());
    this.peerConnection?.close();
    this.audioElement?.remove();
    this.isConnected = false;
    this.onConnectionChange?.("disconnected");
  }
}

// Max Returns Avatar
function MaxReturnsAvatar({ isListening, isSpeaking }) {
  return (
    <div className="relative w-48 h-48 mx-auto">
      <div className={`absolute inset-0 rounded-full bg-gradient-to-r from-cyan-500/20 to-purple-500/20 blur-xl transition-all duration-500 ${isListening || isSpeaking ? "scale-110 opacity-100" : "scale-100 opacity-50"}`} />
      <svg viewBox="0 0 200 200" className="w-full h-full relative z-10">
        <circle cx="100" cy="100" r="95" fill="none" stroke="url(#avatarGradient)" strokeWidth="1" opacity="0.3" />
        <circle cx="100" cy="100" r="85" fill="none" stroke="url(#avatarGradient)" strokeWidth="0.5" strokeDasharray="10 5" className={isListening ? "animate-spin" : ""} style={{ animationDuration: "20s" }} />
        <g stroke="url(#avatarGradient)" strokeWidth="1" fill="none" opacity="0.8">
          <ellipse cx="100" cy="90" rx="45" ry="55" strokeDasharray="3 3" />
          <circle cx="80" cy="80" r="8" className={isListening ? "animate-pulse" : ""} />
          <circle cx="120" cy="80" r="8" className={isListening ? "animate-pulse" : ""} />
          <circle cx="80" cy="80" r="3" fill="url(#avatarGradient)" />
          <circle cx="120" cy="80" r="3" fill="url(#avatarGradient)" />
          <line x1="100" y1="85" x2="100" y2="100" strokeDasharray="2 2" />
          <path d={isSpeaking ? "M 80 115 Q 100 130 120 115" : "M 85 115 Q 100 120 115 115"} strokeDasharray="2 2" />
        </g>
        <defs>
          <linearGradient id="avatarGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#06b6d4" />
            <stop offset="50%" stopColor="#8b5cf6" />
            <stop offset="100%" stopColor="#ec4899" />
          </linearGradient>
        </defs>
      </svg>
      <div className={`absolute bottom-2 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-medium ${isSpeaking ? "bg-purple-500/20 text-purple-300" : isListening ? "bg-cyan-500/20 text-cyan-300" : "bg-slate-700/50 text-slate-400"}`}>
        {isSpeaking ? "Speaking..." : isListening ? "Listening..." : "Ready"}
      </div>
    </div>
  );
}

// Quick Prompt Button
function QuickPromptButton({ icon: Icon, label, onClick, disabled }) {
  return (
    <Button variant="outline" size="sm" onClick={onClick} disabled={disabled} className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white text-xs">
      <Icon className="w-3 h-3 mr-1" />
      {label}
    </Button>
  );
}

export default function AIInvestorPage() {
  const navigate = useNavigate();
  
  // Connection state
  const [connectionStatus, setConnectionStatus] = useState("disconnected");
  const [audioChat, setAudioChat] = useState(null);
  const [sessionMode, setSessionMode] = useState("listen_and_speak"); // "only_listen", "only_speak", "listen_and_speak"
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  
  // Data state
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [settings, setSettings] = useState(null);
  const [knowledge, setKnowledge] = useState([]);
  const [network, setNetwork] = useState([]);
  
  // Modal state
  const [showRoster, setShowRoster] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [newCompany, setNewCompany] = useState({ name: "", description: "", indication: "", modality: "", stage: "", website: "" });
  const [newCompanyDeck, setNewCompanyDeck] = useState(null);
  const [processingNewDeck, setProcessingNewDeck] = useState(false);
  const [newKnowledge, setNewKnowledge] = useState({ title: "", content: "", source_type: "text", source_url: "", category: "general" });
  const [newContact, setNewContact] = useState({ name: "", role: "", organization: "", category: "vc", expertise: "", intro_context: "" });
  const [generatingAnalysis, setGeneratingAnalysis] = useState(null);
  const [showPrivateBriefing, setShowPrivateBriefing] = useState(false);
  const [privateBriefing, setPrivateBriefing] = useState("");

  const [uploadingDeck, setUploadingDeck] = useState(null);

  // Fetch data
  const fetchCompanies = useCallback(async () => {
    const res = await fetch(`${BACKEND_URL}/api/ai-investor/companies`);
    if (res.ok) { const data = await res.json(); setCompanies(data.companies || []); }
  }, []);

  const fetchSettings = useCallback(async () => {
    const res = await fetch(`${BACKEND_URL}/api/ai-investor/settings`);
    if (res.ok) { const data = await res.json(); setSettings(data); }
  }, []);

  const fetchKnowledge = useCallback(async () => {
    const res = await fetch(`${BACKEND_URL}/api/ai-investor/knowledge`);
    if (res.ok) { const data = await res.json(); setKnowledge(data.items || []); }
  }, []);

  const fetchNetwork = useCallback(async () => {
    const res = await fetch(`${BACKEND_URL}/api/ai-investor/network`);
    if (res.ok) { const data = await res.json(); setNetwork(data.contacts || []); }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([fetchCompanies(), fetchSettings(), fetchKnowledge(), fetchNetwork()]);
    };
    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handlers
  const handleMessage = useCallback((message) => {
    if (message.type === "response.audio.delta") setIsSpeaking(true);
    else if (message.type === "response.audio.done") setIsSpeaking(false);
  }, []);

  const handleConnectionChange = useCallback((status) => {
    setConnectionStatus(status);
    if (status === "connected") toast.success("Connected to Max Returns");
    else if (status === "error") toast.error("Failed to connect");
  }, []);

  const startSession = async () => {
    try {
      // Fetch system prompt with company context
      let systemPrompt = "";
      if (selectedCompanyId) {
        const promptRes = await fetch(`${BACKEND_URL}/api/ai-investor/system-prompt?company_id=${selectedCompanyId}`);
        if (promptRes.ok) {
          const promptData = await promptRes.json();
          systemPrompt = promptData.system_prompt;
        }
      } else {
        const promptRes = await fetch(`${BACKEND_URL}/api/ai-investor/system-prompt`);
        if (promptRes.ok) {
          const promptData = await promptRes.json();
          systemPrompt = promptData.system_prompt;
        }
      }
      
      // Add private briefing at the TOP if provided - this takes priority
      if (privateBriefing.trim()) {
        const briefingBlock = `
=======================================================
HIGHEST PRIORITY - PRIVATE INSTRUCTIONS FROM MARCO
=======================================================
Marco (the host) pulled you aside before this session and gave you these MANDATORY instructions. You MUST follow these throughout the entire session:

${privateBriefing}

These instructions override your default behavior. Follow them precisely.
=======================================================

`;
        systemPrompt = briefingBlock + systemPrompt;
      }
      
      // Get voice setting
      const selectedVoice = settings?.voice || "verse";
      
      const chat = new RealtimeAudioChat(handleMessage, handleConnectionChange);
      await chat.init();
      
      // Send system configuration after connection
      if (chat.dataChannel && systemPrompt) {
        // Wait for data channel to be open
        const waitForChannel = () => new Promise(resolve => {
          if (chat.dataChannel.readyState === "open") resolve();
          else chat.dataChannel.addEventListener("open", resolve, { once: true });
        });
        await waitForChannel();
        
        // Configure the session with system prompt
        chat.sendMessage({
          type: "session.update",
          session: {
            instructions: systemPrompt,
            voice: selectedVoice,
            modalities: ["text", "audio"],
            input_audio_transcription: { model: "whisper-1" }
          }
        });
      }
      
      setAudioChat(chat);
      setShowPrivateBriefing(false); // Close the briefing modal
    } catch (err) {
      console.error("Session error:", err);
      toast.error(err.message || "Failed to start voice session");
    }
  };

  const endSession = () => {
    audioChat?.disconnect();
    setAudioChat(null);
    setSessionMode("listen_and_speak");
    setIsListening(false);
    setIsSpeaking(false);
  };

  const setMode = (mode) => {
    if (!audioChat) return;
    setSessionMode(mode);
    
    // Configure mic and speaker based on mode
    const micOn = mode === "only_listen" || mode === "listen_and_speak";
    const speakerOn = mode === "only_speak" || mode === "listen_and_speak";
    
    setIsListening(micOn);
    audioChat.setMicEnabled(micOn);
    audioChat.setSpeakerEnabled(speakerOn);
  };

  const sendQuickPrompt = async (promptType) => {
    const res = await fetch(`${BACKEND_URL}/api/ai-investor/quick-prompt?prompt_type=${promptType}`, { method: "POST" });
    if (res.ok && audioChat?.dataChannel) {
      const data = await res.json();
      audioChat.sendMessage({ type: "conversation.item.create", item: { type: "message", role: "user", content: [{ type: "input_text", text: data.prompt }] } });
      audioChat.sendMessage({ type: "response.create" });
      toast.success("Prompt sent");
    }
  };

  // Company handlers
  const createCompany = async () => {
    if (!newCompany.name) { toast.error("Name required"); return; }
    const res = await fetch(`${BACKEND_URL}/api/ai-investor/companies`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(newCompany)
    });
    if (res.ok) { toast.success("Company added"); setNewCompany({ name: "", description: "", indication: "", modality: "", stage: "", website: "" }); fetchCompanies(); }
  };

  const updateCompany = async (companyId, updates) => {
    await fetch(`${BACKEND_URL}/api/ai-investor/companies/${companyId}`, {
      method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(updates)
    });
    fetchCompanies();
  };

  const deleteCompany = async (companyId) => {
    await fetch(`${BACKEND_URL}/api/ai-investor/companies/${companyId}`, { method: "DELETE" });
    if (selectedCompanyId === companyId) setSelectedCompanyId(null);
    fetchCompanies();
    toast.success("Company removed");
  };

  // Process deck for new company - extracts info and auto-populates fields
  const processNewCompanyDeck = async (file) => {
    setNewCompanyDeck(file);
    setProcessingNewDeck(true);
    
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/ai-investor/analyze-deck`, { method: "POST", body: formData });
      if (res.ok) {
        const data = await res.json();
        // Auto-populate fields from deck analysis
        if (data.extracted_info) {
          setNewCompany(prev => ({
            ...prev,
            name: data.extracted_info.company_name || prev.name,
            description: data.extracted_info.description || prev.description,
            indication: data.extracted_info.indication || prev.indication,
            modality: data.extracted_info.modality || prev.modality,
            stage: data.extracted_info.stage || prev.stage,
          }));
          toast.success("Deck analyzed! Fields auto-populated. Review and add company.");
        }
      } else {
        toast.error("Failed to analyze deck - you can still fill fields manually");
      }
    } catch (err) {
      toast.error("Deck analysis failed - fill fields manually");
    } finally {
      setProcessingNewDeck(false);
    }
  };

  // Create company with deck
  const createCompanyWithDeck = async () => {
    if (!newCompany.name && !newCompanyDeck) { 
      toast.error("Upload a deck or enter company name"); 
      return; 
    }
    
    // Create the company first
    const res = await fetch(`${BACKEND_URL}/api/ai-investor/companies`, {
      method: "POST", 
      headers: { "Content-Type": "application/json" }, 
      body: JSON.stringify(newCompany)
    });
    
    if (res.ok) {
      const data = await res.json();
      const companyId = data.company?.company_id;
      
      // If we have a deck, upload it to the new company
      if (companyId && newCompanyDeck) {
        await uploadDeck(companyId, newCompanyDeck);
      }
      
      toast.success("Company added to roster!");
      setNewCompany({ name: "", description: "", indication: "", modality: "", stage: "", website: "" });
      setNewCompanyDeck(null);
      fetchCompanies();
    } else {
      toast.error("Failed to add company");
    }
  };

  const uploadDeck = async (companyId, file) => {
    setUploadingDeck(companyId);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await fetch(`${BACKEND_URL}/api/ai-investor/companies/${companyId}/upload-deck`, { method: "POST", body: formData });
      if (res.ok) {
        const data = await res.json();
        if (data.auto_populated && Object.keys(data.auto_populated).length > 0) {
          toast.success(`Deck uploaded! Auto-populated: ${Object.keys(data.auto_populated).join(", ")}`);
        } else {
          toast.success("Deck uploaded and analyzed!");
        }
        fetchCompanies();
      } else {
        toast.error("Failed to upload deck");
      }
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploadingDeck(null);
    }
  };

  const generateAnalysis = async (companyId) => {
    setGeneratingAnalysis(companyId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/ai-investor/companies/${companyId}/generate-analysis`, { method: "POST" });
      if (res.ok) { toast.success("Analysis generated"); fetchCompanies(); }
      else toast.error("Failed to generate analysis");
    } finally { setGeneratingAnalysis(null); }
  };

  // Settings handlers
  const updateSettings = async (updates) => {
    const newSettings = { ...settings, ...updates };
    await fetch(`${BACKEND_URL}/api/ai-investor/settings`, {
      method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(newSettings)
    });
    setSettings(newSettings);
  };

  const updatePersonality = (key, value) => {
    updateSettings({ personality: { ...settings?.personality, [key]: value } });
  };

  const updateDimension = (dimId, updates) => {
    const newDims = settings?.dimensions?.map(d => d.dimension_id === dimId ? { ...d, ...updates } : d);
    updateSettings({ dimensions: newDims });
  };

  const resetDimensions = async () => {
    await fetch(`${BACKEND_URL}/api/ai-investor/settings/reset-dimensions`, { method: "POST" });
    fetchSettings();
    toast.success("Dimensions reset");
  };

  // Knowledge handlers
  const addKnowledge = async () => {
    if (!newKnowledge.title) { toast.error("Title required"); return; }
    await fetch(`${BACKEND_URL}/api/ai-investor/knowledge`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(newKnowledge)
    });
    setNewKnowledge({ title: "", content: "", source_type: "text", source_url: "", category: "general" });
    fetchKnowledge();
    toast.success("Knowledge added");
  };

  const deleteKnowledge = async (id) => {
    await fetch(`${BACKEND_URL}/api/ai-investor/knowledge/${id}`, { method: "DELETE" });
    fetchKnowledge();
  };

  // Network handlers
  const addContact = async () => {
    if (!newContact.name) { toast.error("Name required"); return; }
    await fetch(`${BACKEND_URL}/api/ai-investor/network`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(newContact)
    });
    setNewContact({ name: "", role: "", organization: "", category: "vc", expertise: "", intro_context: "" });
    fetchNetwork();
    toast.success("Contact added");
  };

  const deleteContact = async (id) => {
    await fetch(`${BACKEND_URL}/api/ai-investor/network/${id}`, { method: "DELETE" });
    fetchNetwork();
  };

  const selectedCompany = companies.find(c => c.company_id === selectedCompanyId);

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      {/* Header */}
      <nav className="border-b border-slate-800 bg-[#0a0a0f]/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button onClick={() => navigate('/')} variant="ghost" size="icon" className="text-slate-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">Max Returns</h1>
              <p className="text-xs text-slate-500">AI Investor</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Company Selector */}
            <Select value={selectedCompanyId || ""} onValueChange={setSelectedCompanyId}>
              <SelectTrigger className="w-[200px] bg-slate-800 border-slate-700 text-white">
                <SelectValue placeholder="Select company pitching" />
              </SelectTrigger>
              <SelectContent>
                {companies.map(c => (
                  <SelectItem key={c.company_id} value={c.company_id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button data-testid="roster-button" variant="outline" onClick={() => setShowRoster(true)} className="border-slate-700 hover:bg-slate-800 text-white">
              <Users className="w-4 h-4 mr-2" /> Roster
            </Button>
            
            <Button data-testid="settings-button" variant="outline" size="icon" onClick={() => setShowSettings(true)} className="border-slate-700 hover:bg-slate-800 text-white">
              <Settings className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </nav>

      {/* Main */}
      <main className="max-w-5xl mx-auto px-6 py-8">
        {/* Avatar & Status */}
        <div className="text-center mb-8">
          <MaxReturnsAvatar 
            isListening={isListening && (sessionMode === "only_listen" || sessionMode === "listen_and_speak")} 
            isSpeaking={isSpeaking && (sessionMode === "only_speak" || sessionMode === "listen_and_speak")} 
          />
          <h2 className="text-2xl font-bold mt-6 mb-1">Max Returns</h2>
          {selectedCompany && (
            <p className="text-purple-400 text-sm">Evaluating: {selectedCompany.name}</p>
          )}
          <Badge className={`mt-2 ${connectionStatus === "connected" ? "bg-green-500/20 text-green-400" : connectionStatus === "connecting" ? "bg-yellow-500/20 text-yellow-400" : "bg-slate-700 text-slate-400"}`}>
            <Radio className={`w-3 h-3 mr-1 ${connectionStatus === "connected" ? "animate-pulse" : ""}`} />
            {connectionStatus === "connected" ? "Connected" : connectionStatus === "connecting" ? "Connecting..." : "Disconnected"}
          </Badge>
        </div>

        {/* Controls */}
        <Card className="bg-slate-900/50 border-slate-800 mb-6">
          <CardContent className="p-6">
            <div className="flex justify-center gap-4 mb-6">
              {connectionStatus !== "connected" ? (
                <Button onClick={() => setShowPrivateBriefing(true)} disabled={connectionStatus === "connecting"} className="bg-gradient-to-r from-cyan-500 to-purple-500 px-8">
                  {connectionStatus === "connecting" ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Connecting...</> : <><Play className="w-4 h-4 mr-2" /> Start Session</>}
                </Button>
              ) : (
                <Button onClick={endSession} variant="outline" className="border-red-500/50 text-red-400">End Session</Button>
              )}
            </div>

            {/* Mode Selection */}
            {connectionStatus === "connected" && (
              <div className="mb-6">
                <p className="text-xs text-slate-400 mb-3 text-center">Session Mode</p>
                <div className="flex justify-center gap-3">
                  <Button 
                    onClick={() => setMode("only_listen")} 
                    variant={sessionMode === "only_listen" ? "default" : "outline"}
                    className={sessionMode === "only_listen" 
                      ? "bg-cyan-500 hover:bg-cyan-600 text-white" 
                      : "border-slate-600 text-slate-300 hover:bg-slate-800"
                    }
                  >
                    <Mic className="w-4 h-4 mr-2" />
                    Only Listen
                  </Button>
                  <Button 
                    onClick={() => setMode("only_speak")} 
                    variant={sessionMode === "only_speak" ? "default" : "outline"}
                    className={sessionMode === "only_speak" 
                      ? "bg-purple-500 hover:bg-purple-600 text-white" 
                      : "border-slate-600 text-slate-300 hover:bg-slate-800"
                    }
                  >
                    <Volume2 className="w-4 h-4 mr-2" />
                    Only Speak
                  </Button>
                  <Button 
                    onClick={() => setMode("listen_and_speak")} 
                    variant={sessionMode === "listen_and_speak" ? "default" : "outline"}
                    className={sessionMode === "listen_and_speak" 
                      ? "bg-gradient-to-r from-cyan-500 to-purple-500 text-white" 
                      : "border-slate-600 text-slate-300 hover:bg-slate-800"
                    }
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Listen & Speak
                  </Button>
                </div>
              </div>
            )}

            {/* Quick Prompts - show when in speak modes */}
            {connectionStatus === "connected" && (sessionMode === "only_speak" || sessionMode === "listen_and_speak") && (
              <div className="border-t border-slate-800 pt-4">
                <p className="text-xs text-slate-400 mb-3 text-center">Quick Prompts</p>
                <div className="flex flex-wrap justify-center gap-2">
                  <QuickPromptButton icon={Sparkles} label="Witty reply" onClick={() => sendQuickPrompt("witty_reply")} disabled={connectionStatus !== "connected"} />
                  <QuickPromptButton icon={Lightbulb} label="Success story" onClick={() => sendQuickPrompt("success_story")} disabled={connectionStatus !== "connected"} />
                  <QuickPromptButton icon={AlertTriangle} label="Cautionary tale" onClick={() => sendQuickPrompt("cautionary_tale")} disabled={connectionStatus !== "connected"} />
                  <QuickPromptButton icon={Target} label="Recommendations" onClick={() => sendQuickPrompt("final_recommendations")} disabled={connectionStatus !== "connected"} />
                  <QuickPromptButton icon={ThumbsUp} label="Positive feedback" onClick={() => sendQuickPrompt("positive_feedback")} disabled={connectionStatus !== "connected"} />
                  <QuickPromptButton icon={MessageSquare} label="Constructive" onClick={() => sendQuickPrompt("constructive_feedback")} disabled={connectionStatus !== "connected"} />
                  <QuickPromptButton icon={Network} label="Network intros" onClick={() => sendQuickPrompt("network_intro")} disabled={connectionStatus !== "connected"} />
                  <QuickPromptButton icon={Brain} label="Key questions" onClick={() => sendQuickPrompt("key_questions")} disabled={connectionStatus !== "connected"} />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Selected Company Analysis & Prep */}
        {selectedCompany && (selectedCompany.ai_analysis || selectedCompany.deck_prep) && (
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Brain className="w-5 h-5 text-purple-400" />
                AI Prep: {selectedCompany.name}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {selectedCompany.ai_analysis && (
                <>
                  <div>
                    <h4 className="text-sm font-medium text-slate-300 mb-2">Investment Thesis</h4>
                    <p className="text-sm text-slate-400">{selectedCompany.ai_analysis.thesis}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-sm font-medium text-green-400 mb-2">Strengths</h4>
                      <ul className="text-sm text-slate-400 space-y-1">
                        {selectedCompany.ai_analysis.strengths?.map((s, i) => <li key={i}>• {s}</li>)}
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-amber-400 mb-2">Concerns</h4>
                      <ul className="text-sm text-slate-400 space-y-1">
                        {selectedCompany.ai_analysis.concerns?.map((c, i) => <li key={i}>• {c}</li>)}
                      </ul>
                    </div>
                  </div>
                </>
              )}
              
              {/* Deck Prep - Prepared Questions */}
              {selectedCompany.deck_prep?.prepared_questions?.length > 0 && (
                <div className="border-t border-slate-800 pt-4">
                  <h4 className="text-sm font-medium text-cyan-400 mb-2 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" />
                    Prepared Questions for Q&A
                  </h4>
                  <ul className="text-sm text-slate-400 space-y-2">
                    {selectedCompany.deck_prep.prepared_questions.map((q, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="text-cyan-500 mt-1">→</span>
                        <span>{q}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Key Claims to Verify */}
              {selectedCompany.deck_prep?.key_claims?.length > 0 && (
                <div className="border-t border-slate-800 pt-4">
                  <h4 className="text-sm font-medium text-purple-400 mb-2 flex items-center gap-2">
                    <Target className="w-4 h-4" />
                    Key Claims to Verify
                  </h4>
                  <ul className="text-sm text-slate-400 space-y-1">
                    {selectedCompany.deck_prep.key_claims.map((c, i) => <li key={i}>• {c}</li>)}
                  </ul>
                </div>
              )}
              
              {/* Potential Concerns to Probe */}
              {selectedCompany.deck_prep?.potential_concerns?.length > 0 && (
                <div className="border-t border-slate-800 pt-4">
                  <h4 className="text-sm font-medium text-amber-400 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Potential Concerns to Probe
                  </h4>
                  <ul className="text-sm text-slate-400 space-y-1">
                    {selectedCompany.deck_prep.potential_concerns.map((c, i) => <li key={i}>• {c}</li>)}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </main>

      {/* Roster Modal */}
      <Dialog open={showRoster} onOpenChange={setShowRoster}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-3xl max-h-[85vh] overflow-y-auto text-white">
          <DialogHeader>
            <DialogTitle className="text-white">Company Roster</DialogTitle>
          </DialogHeader>
          
          {/* Add Company */}
          <Card className="bg-slate-800/50 border-slate-700 mt-4">
            <CardContent className="p-4 space-y-4">
              {/* Deck Upload - Primary Action */}
              <div className="border-2 border-dashed border-slate-600 rounded-lg p-4 text-center hover:border-purple-500 transition-colors">
                <input 
                  id="new-company-deck" 
                  type="file" 
                  accept=".pdf,.pptx" 
                  className="hidden" 
                  onChange={e => e.target.files[0] && processNewCompanyDeck(e.target.files[0])}
                  disabled={processingNewDeck}
                />
                <Label htmlFor="new-company-deck" className="cursor-pointer block">
                  {processingNewDeck ? (
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="w-8 h-8 text-purple-400 animate-spin" />
                      <span className="text-purple-400 font-medium">Analyzing deck...</span>
                      <span className="text-xs text-slate-500">Extracting company info & preparing AI questions</span>
                    </div>
                  ) : newCompanyDeck ? (
                    <div className="flex flex-col items-center gap-2">
                      <FileText className="w-8 h-8 text-green-400" />
                      <span className="text-green-400 font-medium">{newCompanyDeck.name}</span>
                      <span className="text-xs text-slate-500">Deck ready - review fields below and add company</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <Upload className="w-8 h-8 text-slate-400" />
                      <span className="text-slate-300 font-medium">Upload Pitch Deck</span>
                      <span className="text-xs text-slate-500">PDF or PPTX - AI will analyze and auto-fill fields</span>
                    </div>
                  )}
                </Label>
              </div>
              
              <div className="text-center text-xs text-slate-500">— or enter details manually —</div>
              
              {/* Company Details Form */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-200">Company Name {!newCompanyDeck && "*"}</Label>
                  <Input value={newCompany.name} onChange={e => setNewCompany({...newCompany, name: e.target.value})} className="bg-slate-900 border-slate-700 text-white" placeholder="BioTech Co" />
                </div>
                <div>
                  <Label className="text-slate-200">Indication</Label>
                  <Input value={newCompany.indication} onChange={e => setNewCompany({...newCompany, indication: e.target.value})} className="bg-slate-900 border-slate-700 text-white" placeholder="Oncology" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-slate-200">Modality</Label>
                  <Input value={newCompany.modality} onChange={e => setNewCompany({...newCompany, modality: e.target.value})} className="bg-slate-900 border-slate-700 text-white" placeholder="Small molecule" />
                </div>
                <div>
                  <Label className="text-slate-200">Stage</Label>
                  <Select value={newCompany.stage} onValueChange={v => setNewCompany({...newCompany, stage: v})}>
                    <SelectTrigger className="bg-slate-900 border-slate-700 text-white"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="idea">Idea</SelectItem>
                      <SelectItem value="preclinical">Preclinical</SelectItem>
                      <SelectItem value="phase1">Phase 1</SelectItem>
                      <SelectItem value="phase2">Phase 2</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-200">Website</Label>
                  <Input value={newCompany.website} onChange={e => setNewCompany({...newCompany, website: e.target.value})} className="bg-slate-900 border-slate-700 text-white" placeholder="https://" />
                </div>
              </div>
              <div>
                <Label className="text-slate-200">Description</Label>
                <Textarea value={newCompany.description} onChange={e => setNewCompany({...newCompany, description: e.target.value})} className="bg-slate-900 border-slate-700 text-white" placeholder="Brief description..." rows={2} />
              </div>
              <Button onClick={createCompanyWithDeck} disabled={processingNewDeck} className="w-full bg-gradient-to-r from-cyan-600 to-purple-600 hover:from-cyan-500 hover:to-purple-500">
                {newCompanyDeck ? (
                  <><FileText className="w-4 h-4 mr-2" /> Add Company with Deck</>
                ) : (
                  <><Plus className="w-4 h-4 mr-2" /> Add Company</>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Company List */}
          <div className="space-y-3 mt-4">
            {companies.map(company => (
              <Card key={company.company_id} className="bg-slate-800/30 border-slate-700">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h4 className="font-medium text-white">{company.name}</h4>
                        {company.indication && <Badge variant="outline" className="text-xs text-slate-300">{company.indication}</Badge>}
                        {company.pitch_deck_content && <Badge className="bg-green-500/20 text-green-400 text-xs">Deck</Badge>}
                        {company.deck_prep && <Badge className="bg-cyan-500/20 text-cyan-400 text-xs">Prep Ready</Badge>}
                        {company.ai_analysis && <Badge className="bg-purple-500/20 text-purple-400 text-xs">Analyzed</Badge>}
                      </div>
                      <p className="text-sm text-slate-400 line-clamp-2">{company.description || "No description"}</p>
                      {company.deck_prep?.prepared_questions?.length > 0 && (
                        <p className="text-xs text-cyan-400 mt-1">{company.deck_prep.prepared_questions.length} questions prepared</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      {uploadingDeck === company.company_id ? (
                        <Loader2 className="w-4 h-4 text-purple-400 animate-spin" />
                      ) : (
                        <>
                          <Label htmlFor={`deck-${company.company_id}`} className="cursor-pointer" title="Upload pitch deck (PDF/PPTX) - auto-populates fields & generates prep questions">
                            <Upload className="w-4 h-4 text-slate-400 hover:text-purple-400" />
                          </Label>
                          <input id={`deck-${company.company_id}`} type="file" accept=".pdf,.pptx" className="hidden" onChange={e => e.target.files[0] && uploadDeck(company.company_id, e.target.files[0])} />
                        </>
                      )}
                      <Button variant="ghost" size="icon" onClick={() => generateAnalysis(company.company_id)} disabled={generatingAnalysis === company.company_id} className="text-slate-400 hover:text-cyan-400" title="Generate AI analysis">
                        {generatingAnalysis === company.company_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Brain className="w-4 h-4" />}
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => deleteCompany(company.company_id)} className="text-red-400 hover:text-red-300" title="Delete company">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {companies.length === 0 && <p className="text-center text-slate-500 py-8">No companies yet. Add one above.</p>}
          </div>
        </DialogContent>
      </Dialog>

      {/* Settings Modal */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent data-testid="settings-modal" className="bg-slate-900 border-slate-800 max-w-3xl max-h-[85vh] overflow-y-auto text-white">
          <DialogHeader>
            <DialogTitle className="text-white">AI Investor Settings</DialogTitle>
          </DialogHeader>
          
          <Tabs defaultValue="personality" className="mt-4">
            <TabsList className="bg-slate-800/50 border border-slate-700 grid grid-cols-4 p-1">
              <TabsTrigger value="personality" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400 text-xs"><Brain className="w-3 h-3 mr-1" />Personality</TabsTrigger>
              <TabsTrigger value="dimensions" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400 text-xs"><Scale className="w-3 h-3 mr-1" />Dimensions</TabsTrigger>
              <TabsTrigger value="knowledge" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400 text-xs"><Database className="w-3 h-3 mr-1" />Knowledge</TabsTrigger>
              <TabsTrigger value="network" className="data-[state=active]:bg-slate-700 data-[state=active]:text-white text-slate-400 text-xs"><Network className="w-3 h-3 mr-1" />Network</TabsTrigger>
            </TabsList>

            {/* Personality Tab */}
            <TabsContent value="personality" className="space-y-4 mt-4">
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-4 space-y-5">
                  {/* Voice Selection */}
                  <div className="pb-4 border-b border-slate-700">
                    <div className="flex items-center gap-2 mb-3">
                      <AudioLines className="w-4 h-4 text-purple-400" />
                      <Label className="text-slate-200 font-medium">Voice</Label>
                    </div>
                    <Select value={settings?.voice || "verse"} onValueChange={v => updateSettings({ voice: v })}>
                      <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                        <SelectValue placeholder="Select voice" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="alloy">Alloy - Neutral & balanced</SelectItem>
                        <SelectItem value="ash">Ash - Soft & warm</SelectItem>
                        <SelectItem value="ballad">Ballad - Warm & engaging</SelectItem>
                        <SelectItem value="coral">Coral - Clear & expressive</SelectItem>
                        <SelectItem value="echo">Echo - Smooth & resonant</SelectItem>
                        <SelectItem value="sage">Sage - Calm & authoritative</SelectItem>
                        <SelectItem value="shimmer">Shimmer - Bright & lively</SelectItem>
                        <SelectItem value="verse">Verse - Confident & articulate</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500 mt-2">Choose how Max Returns sounds during voice conversations. Changes take effect on next connection.</p>
                  </div>
                  
                  {/* Personality Sliders */}
                  {[
                    { key: "directness", label: "Directness", left: "Diplomatic", right: "Blunt" },
                    { key: "humor_level", label: "Humor", left: "Serious", right: "Witty" },
                    { key: "technical_depth", label: "Technical Depth", left: "High-level", right: "Deep dive" },
                    { key: "encouragement_vs_challenge", label: "Feedback Style", left: "Encouraging", right: "Challenging" },
                    { key: "risk_tolerance", label: "Risk View", left: "Conservative", right: "Bold" },
                  ].map(({ key, label, left, right }) => (
                    <div key={key}>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-slate-400">{left}</span>
                        <span className="text-slate-200 font-medium">{label}</span>
                        <span className="text-slate-400">{right}</span>
                      </div>
                      <Slider value={[settings?.personality?.[key] || 50]} onValueChange={([v]) => updatePersonality(key, v)} max={100} step={5} className="w-full" />
                    </div>
                  ))}
                  <div className="pt-4">
                    <Label className="text-slate-200">Custom Instructions</Label>
                    <Textarea value={settings?.personality?.custom_instructions || ""} onChange={e => updatePersonality("custom_instructions", e.target.value)} className="bg-slate-900 border-slate-700 text-white mt-2" placeholder="Additional personality details..." rows={3} />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Dimensions Tab */}
            <TabsContent value="dimensions" className="space-y-4 mt-4">
              <div className="flex justify-end">
                <Button variant="outline" size="sm" onClick={resetDimensions} className="border-slate-700 text-slate-300">
                  <RefreshCw className="w-3 h-3 mr-1" /> Reset to Defaults
                </Button>
              </div>
              <div className="space-y-3">
                {settings?.dimensions?.map(dim => (
                  <Card key={dim.dimension_id} className={`border-slate-700 ${dim.enabled ? "bg-slate-800/50" : "bg-slate-900/30 opacity-60"}`}>
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <input type="checkbox" checked={dim.enabled} onChange={e => updateDimension(dim.dimension_id, { enabled: e.target.checked })} className="rounded" />
                          <span className="font-medium text-white text-sm">{dim.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-400">Weight:</span>
                          <Select value={String(dim.weight)} onValueChange={v => updateDimension(dim.dimension_id, { weight: parseInt(v) })}>
                            <SelectTrigger className="w-16 h-7 bg-slate-900 border-slate-700 text-white text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {[1,2,3,4,5,6,7,8,9,10].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <p className="text-xs text-slate-400">{dim.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* Knowledge Tab */}
            <TabsContent value="knowledge" className="space-y-4 mt-4">
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-slate-200">Title</Label>
                      <Input value={newKnowledge.title} onChange={e => setNewKnowledge({...newKnowledge, title: e.target.value})} className="bg-slate-900 border-slate-700 text-white" placeholder="Knowledge title" />
                    </div>
                    <div>
                      <Label className="text-slate-200">Category</Label>
                      <Select value={newKnowledge.category} onValueChange={v => setNewKnowledge({...newKnowledge, category: v})}>
                        <SelectTrigger className="bg-slate-900 border-slate-700 text-white"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="success_story">Success Story</SelectItem>
                          <SelectItem value="cautionary_tale">Cautionary Tale</SelectItem>
                          <SelectItem value="market_insight">Market Insight</SelectItem>
                          <SelectItem value="general">General</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-slate-200">Source Type</Label>
                      <Select value={newKnowledge.source_type} onValueChange={v => setNewKnowledge({...newKnowledge, source_type: v})}>
                        <SelectTrigger className="bg-slate-900 border-slate-700 text-white"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="text">Text</SelectItem>
                          <SelectItem value="url">URL</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {newKnowledge.source_type === "url" && (
                      <div>
                        <Label className="text-slate-200">URL</Label>
                        <Input value={newKnowledge.source_url} onChange={e => setNewKnowledge({...newKnowledge, source_url: e.target.value})} className="bg-slate-900 border-slate-700 text-white" placeholder="https://" />
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="text-slate-200">Content</Label>
                    <Textarea value={newKnowledge.content} onChange={e => setNewKnowledge({...newKnowledge, content: e.target.value})} className="bg-slate-900 border-slate-700 text-white" placeholder="Knowledge content..." rows={3} />
                  </div>
                  <Button onClick={addKnowledge} className="w-full"><Plus className="w-4 h-4 mr-2" /> Add Knowledge</Button>
                </CardContent>
              </Card>
              <div className="space-y-2">
                {knowledge.map(item => (
                  <Card key={item.knowledge_id} className="bg-slate-800/30 border-slate-700">
                    <CardContent className="p-3 flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-white text-sm">{item.title}</h4>
                          <Badge variant="outline" className="text-xs text-slate-300">{item.category}</Badge>
                          {item.source_type === "url" && <Link className="w-3 h-3 text-slate-400" />}
                        </div>
                        <p className="text-xs text-slate-400 line-clamp-1 mt-1">{item.content}</p>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => deleteKnowledge(item.knowledge_id)} className="text-red-400"><Trash2 className="w-4 h-4" /></Button>
                    </CardContent>
                  </Card>
                ))}
                {knowledge.length === 0 && <p className="text-center text-slate-500 py-4">No knowledge items yet.</p>}
              </div>
            </TabsContent>

            {/* Network Tab */}
            <TabsContent value="network" className="space-y-4 mt-4">
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-4 space-y-3">
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-slate-200">Name</Label>
                      <Input value={newContact.name} onChange={e => setNewContact({...newContact, name: e.target.value})} className="bg-slate-900 border-slate-700 text-white" placeholder="Dr. Jane Smith" />
                    </div>
                    <div>
                      <Label className="text-slate-200">Role</Label>
                      <Input value={newContact.role} onChange={e => setNewContact({...newContact, role: e.target.value})} className="bg-slate-900 border-slate-700 text-white" placeholder="Partner" />
                    </div>
                    <div>
                      <Label className="text-slate-200">Organization</Label>
                      <Input value={newContact.organization} onChange={e => setNewContact({...newContact, organization: e.target.value})} className="bg-slate-900 border-slate-700 text-white" placeholder="VC Fund" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-slate-200">Category</Label>
                      <Select value={newContact.category} onValueChange={v => setNewContact({...newContact, category: v})}>
                        <SelectTrigger className="bg-slate-900 border-slate-700 text-white"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="vc">VC / Investor</SelectItem>
                          <SelectItem value="pharma_bd">Pharma BD</SelectItem>
                          <SelectItem value="kol">KOL</SelectItem>
                          <SelectItem value="regulatory">Regulatory</SelectItem>
                          <SelectItem value="cmo">CMO</SelectItem>
                          <SelectItem value="scientific_advisor">Scientific Advisor</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-slate-200">Expertise</Label>
                      <Input value={newContact.expertise} onChange={e => setNewContact({...newContact, expertise: e.target.value})} className="bg-slate-900 border-slate-700 text-white" placeholder="Oncology, Gene Therapy" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-slate-200">When to Recommend</Label>
                    <Input value={newContact.intro_context} onChange={e => setNewContact({...newContact, intro_context: e.target.value})} className="bg-slate-900 border-slate-700 text-white" placeholder="Good for early-stage oncology..." />
                  </div>
                  <Button onClick={addContact} className="w-full"><Plus className="w-4 h-4 mr-2" /> Add Contact</Button>
                </CardContent>
              </Card>
              <div className="space-y-2">
                {network.map(contact => (
                  <Card key={contact.contact_id} className="bg-slate-800/30 border-slate-700">
                    <CardContent className="p-3 flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-slate-400" />
                          <h4 className="font-medium text-white text-sm">{contact.name}</h4>
                          <Badge variant="outline" className="text-xs text-slate-300">{contact.category}</Badge>
                        </div>
                        <p className="text-xs text-slate-400 mt-1">{contact.role} at {contact.organization}</p>
                        {contact.expertise && <p className="text-xs text-slate-500">Expertise: {contact.expertise}</p>}
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => deleteContact(contact.contact_id)} className="text-red-400"><Trash2 className="w-4 h-4" /></Button>
                    </CardContent>
                  </Card>
                ))}
                {network.length === 0 && <p className="text-center text-slate-500 py-4">No contacts yet.</p>}
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Private Briefing Modal */}
      <Dialog open={showPrivateBriefing} onOpenChange={setShowPrivateBriefing}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-lg text-white">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-purple-400" />
              Private Briefing with Max
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-400">
              Before the session starts, you can give Max specific instructions on how to behave. 
              This is like pulling him aside for a quick private chat.
            </p>
            
            <div>
              <Label className="text-slate-200">Your instructions for Max:</Label>
              <Textarea 
                value={privateBriefing} 
                onChange={e => setPrivateBriefing(e.target.value)} 
                className="bg-slate-800 border-slate-700 text-white mt-2 min-h-[150px]" 
                placeholder="e.g., Focus on asking about their competitive landscape... Be extra encouraging with this founder, they're nervous... Challenge their market size assumptions... Ask about their exit strategy..."
              />
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button 
                variant="outline" 
                onClick={() => { setPrivateBriefing(""); startSession(); }} 
                className="flex-1 border-slate-700 text-slate-300"
              >
                Skip Briefing
              </Button>
              <Button 
                onClick={startSession} 
                disabled={connectionStatus === "connecting"}
                className="flex-1 bg-gradient-to-r from-cyan-500 to-purple-500"
              >
                {connectionStatus === "connecting" ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Connecting...</>
                ) : (
                  <><Play className="w-4 h-4 mr-2" /> Start Session</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
